import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertProps,
  Box,
  Button,
  Container,
  FormField,
  Header,
  Input,
  InputProps,
  Link,
  Modal,
  Pagination,
  SpaceBetween,
  Spinner,
  Table,
  TextFilter,
  TextFilterProps,
  Tiles,
} from "@amzn/awsui-components-react";
import {
  OmsPresetMetadata,
  OmsPresetScope,
  OmsExportPreset,
} from "../../lusca-search-api/generated-src";
import { OmsPresetsState, presetErrorMessage } from "../../hooks/useOmsPresets";
import { PresetRetryExpiredError } from "../../hooks/omsPresetRetry";
import { reportTimestamp } from "./OMSReportSchedule";
import { presetScopeLabel } from "./omsPresetEditing";
import { comparisonLabel } from "./omsReportFormatting";
import { presetNameError } from "./omsConfigurationValidation";

export function OMSPresetSave({
  name,
  scope,
  onName,
  onScope,
  validFilters,
  validColumns,
  attempted,
  nameRef,
  disabled,
  reviewing = false,
  currentPresetName,
  onDestination,
}: {
  name: string;
  scope: OmsPresetScope;
  onName: (name: string) => void;
  onScope: (scope: OmsPresetScope) => void;
  validFilters: boolean;
  validColumns: boolean;
  attempted: boolean;
  nameRef: React.RefObject<InputProps.Ref>;
  disabled: boolean;
  reviewing?: boolean;
  currentPresetName?: string;
  onDestination: (updating: boolean) => void;
}) {
  const [nameTouched, setNameTouched] = useState(false);
  const invalidRows = scope !== "columns" && !validFilters;
  const invalidColumns = scope !== "filters" && !validColumns;
  return (
    <SpaceBetween size="m">
      {currentPresetName ? (
        <FormField
          stretch
          className="oms-preset-save__destination"
          label="Save to"
          constraintText="Presets are private to you."
        >
          <Tiles
            columns={2}
            value={reviewing ? "update" : "new"}
            items={[
              {
                value: "update",
                label: `Update “${currentPresetName}”`,
                disabled,
              },
              { value: "new", label: "Create a new preset", disabled },
            ]}
            onChange={({ detail }) => onDestination(detail.value === "update")}
          />
        </FormField>
      ) : (
        <Box color="text-body-secondary">Presets are private to you.</Box>
      )}
      <div className="oms-preset-name">
        <FormField
          label="Preset name"
          constraintText="Use a unique name. Up to 80 characters."
          errorText={
            attempted || nameTouched ? presetNameError(name) : undefined
          }
        >
          <Input
            ref={nameRef}
            autoFocus
            disabled={disabled}
            ariaLabel="Preset name"
            value={name}
            onChange={({ detail }) => onName(detail.value)}
            onBlur={() => setNameTouched(true)}
          />
        </FormField>
      </div>
      <FormField
        stretch
        className="oms-preset-save__scope"
        label="Settings to save"
      >
        <Tiles
          columns={3}
          value={scope}
          items={[
            { value: "both", label: "Rows and columns", disabled },
            { value: "filters", label: "Rows only", disabled },
            { value: "columns", label: "Columns only", disabled },
          ]}
          onChange={({ detail }) => onScope(detail.value as OmsPresetScope)}
        />
      </FormField>
      {(invalidRows || invalidColumns) && (
        <SpaceBetween size="s">
          {invalidRows && (
            <Alert type="warning">
              Correct the active filters before saving them.
            </Alert>
          )}
          {invalidColumns && (
            <Alert type="warning">
              Correct the column selection before saving it.
            </Alert>
          )}
        </SpaceBetween>
      )}
    </SpaceBetween>
  );
}

type PresetManagement = {
  state: OmsPresetsState;
  confirmInModal: boolean;
  readOnly: boolean;
  rename?: { id: string; name: string };
  setRename: React.Dispatch<
    React.SetStateAction<{ id: string; name: string } | undefined>
  >;
  deleting?: string;
  setDeleting: React.Dispatch<React.SetStateAction<string | undefined>>;
  input: React.RefObject<InputProps.Ref>;
  onDeleted: (id: string) => void;
  onRenamed: (preset: OmsPresetMetadata) => void;
  onEdit: (id: string) => void;
  onPreview: (id: string) => void;
  onCopy?: (id: string) => void;
};

function presetNameCell(preset: OmsPresetMetadata, controls: PresetManagement) {
  const { rename, state, input, setRename } = controls;
  if (!controls.confirmInModal && rename?.id === preset.presetId)
    return (
      <FormField label="New name">
        <Input
          ref={input}
          autoFocus
          ariaLabel={`Rename ${preset.name}`}
          value={rename.name}
          disabled={state.change.isLoading}
          onChange={({ detail }) =>
            setRename({ ...rename, name: detail.value })
          }
        />
      </FormField>
    );
  const name = controls.confirmInModal ? (
    <Link
      color="normal"
      href={`#preset-${preset.presetId}`}
      ariaLabel={`View saved settings for ${preset.name}`}
      onFollow={(event) => {
        event.preventDefault();
        if (!state.read.isLoading && !state.change.isLoading)
          controls.onPreview(preset.presetId);
      }}
    >
      {preset.name}
    </Link>
  ) : (
    preset.name
  );
  return (
    <div className="oms-preset-table-name-row">
      <Box fontWeight="bold">
        <span className="oms-preset-table-name">{name}</span>
      </Box>
      {controls.confirmInModal &&
        state.read.isLoading &&
        state.read.variables === preset.presetId && (
          <span
            role="status"
            aria-label={`Loading saved settings for ${preset.name}`}
            className="oms-preset-table-spinner"
          >
            <Spinner />
          </span>
        )}
    </div>
  );
}

function presetActionsCell(
  preset: OmsPresetMetadata,
  controls: PresetManagement
) {
  const {
    rename,
    state,
    input,
    setRename,
    deleting,
    setDeleting,
    onDeleted,
    onRenamed,
    onEdit,
  } = controls;
  if (!controls.confirmInModal && rename?.id === preset.presetId)
    return (
      <SpaceBetween direction="horizontal" size="xs">
        <Button
          disabled={state.change.isLoading}
          onClick={() => setRename(undefined)}
        >
          Cancel
        </Button>
        <Button
          disabled={!rename.name.trim()}
          loading={state.change.isLoading}
          loadingText="Renaming"
          onClick={() => {
            state.change
              .mutateAsync({
                action: "rename",
                existing: preset,
                name: rename.name,
              })
              .then((result) => {
                if (result.preset) onRenamed(result.preset);
                setRename(undefined);
              })
              .catch(() => input.current?.focus());
          }}
        >
          Rename preset
        </Button>
      </SpaceBetween>
    );
  if (!controls.confirmInModal && deleting === preset.presetId)
    return (
      <SpaceBetween size="xs">
        <Box>Delete “{preset.name}”?</Box>
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            disabled={state.change.isLoading}
            onClick={() => setDeleting(undefined)}
          >
            Cancel
          </Button>
          <Button
            loading={state.change.isLoading}
            loadingText="Deleting"
            onClick={() => {
              state.change
                .mutateAsync({ action: "delete", existing: preset })
                .then(() => {
                  onDeleted(preset.presetId);
                  setDeleting(undefined);
                })
                .catch(() => undefined);
            }}
          >
            Delete preset
          </Button>
        </SpaceBetween>
      </SpaceBetween>
    );
  return (
    <div className="oms-preset-actions">
      <div className="oms-preset-actions__settings">
        <Button
          disabled={state.change.isLoading || state.read.isLoading}
          onClick={() => onEdit(preset.presetId)}
        >
          {controls.readOnly ? "View settings" : "Edit preset"}
        </Button>
        {controls.onCopy && (
          <Button
            ariaLabel={`Reuse preset ${preset.name} to another GL or account`}
            disabled={state.change.isLoading || state.read.isLoading}
            onClick={() => controls.onCopy?.(preset.presetId)}
          >
            Reuse preset
          </Button>
        )}
      </div>
      <div
        className="oms-preset-actions__management"
        role="group"
        aria-label={`Manage ${preset.name}`}
      >
        <Button
          iconName="edit"
          ariaLabel={`Rename preset ${preset.name}`}
          disabled={state.change.isLoading || state.read.isLoading}
          onClick={() => {
            state.change.reset();
            setDeleting(undefined);
            setRename({ id: preset.presetId, name: preset.name });
          }}
        />
        <Button
          iconName="remove"
          ariaLabel={`Delete preset ${preset.name}`}
          disabled={state.change.isLoading || state.read.isLoading}
          onClick={() => {
            state.change.reset();
            setRename(undefined);
            setDeleting(preset.presetId);
          }}
        />
      </div>
    </div>
  );
}

export function OMSPresetManager({
  state,
  onDeleted,
  onRenamed,
  onEdit,
  onCopy,
  confirmInModal = false,
  readOnly = false,
}: {
  state: OmsPresetsState;
  confirmInModal?: boolean;
  readOnly?: boolean;
  onDeleted: (id: string) => void;
  onRenamed: (preset: OmsPresetMetadata) => void;
  onEdit: (id: string) => void;
  onCopy?: (id: string) => void;
}) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rename, setRename] = useState<{ id: string; name: string }>();
  const [deleting, setDeleting] = useState<string>();
  const [preview, setPreview] = useState<OmsExportPreset>();
  const previewRequest = useRef<string>();
  const previewOrigin = useRef<HTMLElement | null>(null);
  const [sorting, setSorting] = useState({
    field: confirmInModal ? "updatedAt" : "name",
    descending: confirmInModal,
  });
  const [renameAttempted, setRenameAttempted] = useState(false);
  const errorControl = useRef<AlertProps.Ref>(null);
  const input = useRef<InputProps.Ref>(null);
  const searchControl = useRef<TextFilterProps.Ref>(null);
  useEffect(() => {
    if (!confirmInModal) searchControl.current?.focus();
  }, [confirmInModal]);
  useEffect(() => {
    setRenameAttempted(false);
  }, [rename?.id]);
  useEffect(() => {
    if (state.change.isError || state.read.isError)
      errorControl.current?.focus();
  }, [
    state.change.error,
    state.change.isError,
    state.read.error,
    state.read.isError,
  ]);
  const openPreview = (id: string, rememberOrigin = true) => {
    previewRequest.current = id;
    if (rememberOrigin)
      previewOrigin.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
    state.read
      .mutateAsync(id)
      .then(setPreview)
      .catch(() => undefined);
  };
  const closePreview = () => {
    setPreview(undefined);
    window.setTimeout(() => {
      if (previewOrigin.current?.isConnected) previewOrigin.current.focus();
      else searchControl.current?.focus();
    }, 0);
  };
  const openPreset = (id: string) => {
    if (!readOnly) onEdit(id);
    else openPreview(id);
  };
  const controls = {
    state,
    confirmInModal,
    readOnly,
    rename,
    setRename,
    deleting,
    setDeleting,
    input,
    onDeleted: (id: string) => {
      onDeleted(id);
      searchControl.current?.focus();
    },
    onRenamed: (preset: OmsPresetMetadata) => {
      onRenamed(preset);
      searchControl.current?.focus();
    },
    onEdit: openPreset,
    onPreview: openPreview,
    onCopy,
  };
  const items = (state.list.data || [])
    .filter((preset) =>
      preset.name.toLocaleLowerCase().includes(search.toLocaleLowerCase())
    )
    .sort((a, b) => {
      const difference =
        sorting.field === "name"
          ? a.name.localeCompare(b.name)
          : Date.parse(a.updatedAt) - Date.parse(b.updatedAt);
      if (!difference) return a.name.localeCompare(b.name);
      return sorting.descending ? -difference : difference;
    });
  const pages = Math.max(1, Math.ceil(items.length / 10));
  const currentPage = Math.min(page, pages);
  let recoveryAction: React.ReactNode;
  if (
    state.change.error instanceof PresetRetryExpiredError &&
    state.change.error.reconciled
  )
    recoveryAction = (
      <Button onClick={() => state.restartExpiredRequest()}>
        Start new request
      </Button>
    );
  else if (state.read.isError)
    recoveryAction = (
      <Button
        onClick={() => {
          if (previewRequest.current)
            openPreview(previewRequest.current, false);
          else if (state.read.variables) openPreset(state.read.variables);
        }}
      >
        Load preset again
      </Button>
    );
  else
    recoveryAction = (
      <Button
        onClick={() => {
          state.list.refetch().catch(() => undefined);
        }}
      >
        Reload presets
      </Button>
    );
  return (
    <SpaceBetween size="m">
      {/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- Bounded saved-setting lists need keyboard scroll targets. */}
      {preview && (
        <Modal
          visible
          size="large"
          header={preview.name}
          closeAriaLabel="Close preset settings"
          onDismiss={closePreview}
          footer={
            <div className="oms-preset-preview-actions">
              <Button onClick={closePreview}>Close</Button>
              {!readOnly && (
                <Button
                  variant="primary"
                  onClick={() => onEdit(preview.presetId)}
                >
                  Edit preset
                </Button>
              )}
            </div>
          }
        >
          <SpaceBetween size="m">
            {preview.scope !== "columns" && (
              <section aria-label="Saved rows">
                <Container header={<Header variant="h3">Rows</Header>}>
                  <Box fontWeight="bold">
                    {preview.rows === "all"
                      ? "All rows"
                      : preview.operation === "or"
                      ? "Any condition (OR)"
                      : "All conditions (AND)"}
                  </Box>
                  {preview.rows !== "all" && (
                    // Long saved conditions must be scrollable with the keyboard.
                    <ul
                      className="oms-settings-summary__list"
                      aria-label="Saved filter conditions"
                      tabIndex={0}
                    >
                      {(preview.filters || []).map((filter, index) => (
                        // Duplicate conditions are valid.
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index}>
                          <strong>{filter.column}</strong>{" "}
                          {comparisonLabel(
                            filter.operator,
                            preview.schemaTypes[filter.column]
                          ).toLowerCase()}{" "}
                          {filter.values
                            ? filter.values
                                .map((value) => JSON.stringify(value))
                                .join(", ")
                            : filter.value !== undefined
                            ? JSON.stringify(filter.value)
                            : ""}
                        </li>
                      ))}
                    </ul>
                  )}
                </Container>
              </section>
            )}
            {preview.scope !== "filters" && (
              <section aria-label="Saved columns">
                <Container header={<Header variant="h3">Columns</Header>}>
                  <Box fontWeight="bold">
                    {preview.columns?.strategy === "all"
                      ? "All columns"
                      : "Selected columns"}
                  </Box>
                  {preview.columns?.strategy === "selected" && (
                    // The bounded ordered list is a keyboard scroll target.
                    <ol
                      className="oms-settings-summary__list"
                      aria-label="Saved column order"
                      tabIndex={0}
                    >
                      {preview.columns.names?.map((name) => (
                        <li key={name}>{name}</li>
                      ))}
                    </ol>
                  )}
                </Container>
              </section>
            )}
          </SpaceBetween>
        </Modal>
      )}
      {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
      {confirmInModal &&
        (rename || deleting) &&
        (() => {
          const target = (state.list.data || []).find(
            (item) => item.presetId === (rename?.id || deleting)
          );
          if (!target) return null;
          const dismiss = () => {
            if (state.change.isLoading) return;
            setRename(undefined);
            setDeleting(undefined);
            state.change.reset();
            searchControl.current?.focus();
          };
          const submit = () => {
            setRenameAttempted(true);
            if (rename && presetNameError(rename.name)) {
              input.current?.focus();
              return;
            }
            state.change
              .mutateAsync(
                rename
                  ? { action: "rename", existing: target, name: rename.name }
                  : { action: "delete", existing: target }
              )
              .then((result) => {
                if (result.preset) controls.onRenamed(result.preset);
                else controls.onDeleted(target.presetId);
                setRename(undefined);
                setDeleting(undefined);
              })
              .catch(() => input.current?.focus());
          };
          return (
            <Modal
              visible
              size="medium"
              header={rename ? "Rename preset" : `Delete “${target.name}”?`}
              closeAriaLabel="Cancel preset change"
              onDismiss={dismiss}
              footer={
                <Box float="right">
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button disabled={state.change.isLoading} onClick={dismiss}>
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      loading={state.change.isLoading}
                      onClick={submit}
                    >
                      {rename ? "Rename preset" : "Delete preset"}
                    </Button>
                  </SpaceBetween>
                </Box>
              }
            >
              <SpaceBetween size="m">
                {state.change.isError && (
                  <Alert
                    ref={errorControl}
                    type="error"
                    action={recoveryAction}
                  >
                    {presetErrorMessage(state.change.error)}
                  </Alert>
                )}
                {rename ? (
                  <FormField
                    label="Preset name"
                    constraintText="Use a unique name. Up to 80 characters."
                    errorText={
                      renameAttempted ? presetNameError(rename.name) : undefined
                    }
                  >
                    <Input
                      ref={input}
                      autoFocus
                      value={rename.name}
                      ariaLabel="Preset name"
                      disabled={state.change.isLoading}
                      onChange={({ detail }) =>
                        setRename({ ...rename, name: detail.value })
                      }
                      onBlur={() => setRenameAttempted(true)}
                    />
                  </FormField>
                ) : (
                  <Box>
                    This deletes the saved preset. This action cannot be undone.
                  </Box>
                )}
              </SpaceBetween>
            </Modal>
          );
        })()}

      {(state.list.isError ||
        ((!confirmInModal || (!rename && !deleting)) && state.change.isError) ||
        state.read.isError) && (
        <Alert
          ref={errorControl}
          type="error"
          header="Presets unavailable"
          action={recoveryAction}
        >
          {presetErrorMessage(
            state.change.error || state.read.error || state.list.error
          )}
        </Alert>
      )}
      <Table<OmsPresetMetadata>
        wrapLines
        variant={confirmInModal ? "container" : "embedded"}
        className={confirmInModal ? "oms-workflow-section" : undefined}
        header={
          <Header
            variant="h2"
            counter={
              state.list.data ? `(${state.list.data.length})` : undefined
            }
            description={
              confirmInModal
                ? "Your reusable export settings. Select a preset name to view its saved settings."
                : undefined
            }
            actions={
              confirmInModal && (
                <Button
                  iconName="refresh"
                  ariaLabel="Refresh presets"
                  loading={state.list.isFetching}
                  disabled={state.change.isLoading || state.read.isLoading}
                  onClick={() => {
                    state.list.refetch().catch(() => undefined);
                  }}
                />
              )
            }
          >
            Saved Export Presets
          </Header>
        }
        ariaLabels={{ tableLabel: "Saved Export Presets" }}
        loading={state.list.isLoading}
        loadingText="Loading your presets"
        items={items.slice((currentPage - 1) * 10, currentPage * 10)}
        trackBy="presetId"
        sortingColumn={{ sortingField: sorting.field }}
        sortingDescending={sorting.descending}
        onSortingChange={({ detail }) => {
          const field = detail.sortingColumn.sortingField;
          if (field === "name" || field === "updatedAt") {
            setSorting({ field, descending: Boolean(detail.isDescending) });
            setPage(1);
          }
        }}
        filter={
          <TextFilter
            ref={searchControl}
            filteringText={search}
            filteringPlaceholder="Search preset names"
            filteringAriaLabel="Search preset names"
            filteringClearAriaLabel="Clear preset search"
            onChange={({ detail }) => {
              setSearch(detail.filteringText);
              setPage(1);
            }}
          />
        }
        pagination={
          items.length > 10 ? (
            <Pagination
              currentPageIndex={currentPage}
              pagesCount={pages}
              onChange={({ detail }) => setPage(detail.currentPageIndex)}
              ariaLabels={{
                nextPageLabel: "Next preset page",
                previousPageLabel: "Previous preset page",
                pageLabel: (index) => `Preset page ${index}`,
              }}
            />
          ) : undefined
        }
        empty={
          <Box textAlign="center" padding="l">
            <SpaceBetween size="s">
              <Box fontWeight="bold">
                {search ? "No matching presets" : "No presets saved yet"}
              </Box>
              {search ? (
                <Button
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                    searchControl.current?.focus();
                  }}
                >
                  Clear search
                </Button>
              ) : (
                <Box color="text-body-secondary">
                  Save your settings from Export to reuse them here.
                </Box>
              )}
            </SpaceBetween>
          </Box>
        }
        columnDefinitions={[
          {
            id: "name",
            header: "Name",
            sortingField: "name",
            minWidth: 180,
            isRowHeader: true,
            cell: (preset) => presetNameCell(preset, controls),
          },
          {
            id: "scope",
            header: "Settings",
            width: 160,
            minWidth: 144,
            cell: (preset) => presetScopeLabel[preset.scope],
          },
          {
            id: "updated",
            header: "Last edited",
            sortingField: "updatedAt",
            width: 192,
            minWidth: 176,
            cell: (preset) => reportTimestamp(preset.updatedAt),
          },
          {
            id: "actions",
            header: "Actions",
            width: controls.onCopy ? 360 : 224,
            minWidth: controls.onCopy ? 352 : 224,
            cell: (preset) => presetActionsCell(preset, controls),
          },
        ]}
      />
    </SpaceBetween>
  );
}
